import { useState, useMemo, useCallback } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { DataTable } from "@/components/ui/DataTable/index";
import { TradingContract, TradingAccount } from "@/lib/api/otc";
import type { RowBgColorResult } from "@/components/ui/DataTable/types";
import { Customer } from "@/lib/api/reports";
import { getOtcContractColumns } from "../shared/OtcContractColumns";
import { useOtcContractHandlers } from "../shared/useOtcContractHandlers";
import { OtcContractModals } from "../shared/OtcContractModals";
import { OtcContractFormModal } from "../shared/OtcContractFormModal";
import { OtcContractDetailModal } from "@/section/otc/contract-detail-view";
import { useBearStore } from "@/section/otc/ContractForm";
import { CopyIcon, RotateCcw, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";

interface OtcContractContentProps {
  showTitle?: boolean;
  contracts: TradingContract[];
  stagedContracts: TradingContract[];
  customers: Customer[];
  accounts: TradingAccount[];
  refetchContracts: () => void;
}

export function OtcContractContent({
  showTitle = true,
  contracts,
  stagedContracts,
  customers,
  accounts,
  refetchContracts,
}: OtcContractContentProps) {
  const [tabValue, setTabValue] = useState("unstaged");
  const [isOpenRolledFilterActive, setIsOpenRolledFilterActive] = useState(false);
  const [detailContractId, setDetailContractId] = useState<string | null>(null);

  // Filter for "Open Rolled" contracts: is_contract_open AND rolled_option_month is not falsy
  const filteredContracts = useMemo(() => {
    if (!isOpenRolledFilterActive) return contracts;
    return contracts.filter(
      (contract) =>
        contract.is_contract_open === true && contract.rolled_option_month
    );
  }, [contracts, isOpenRolledFilterActive]);

  const filteredStagedContracts = useMemo(() => {
    if (!isOpenRolledFilterActive) return stagedContracts;
    return stagedContracts.filter(
      (contract) =>
        contract.is_contract_open === true && contract.rolled_option_month
    );
  }, [stagedContracts, isOpenRolledFilterActive]);

  const {
    isUnstaging,
    isDeleting,
    unstageModalId,
    deleteModalId,
    setUnstageModalId,
    setDeleteModalId,
    confirmUnstagePrompt,
    confirmDeletePrompt,
    onUnstage,
    onDelete,
  } = useOtcContractHandlers();

  const contractRowBgColor = useCallback((row: TradingContract): RowBgColorResult | undefined => {
    switch (row.source_type) {
      case "form":
        return { preset: 'light-green' as const };
      case "tt_fill":
        return { preset: 'light-blue' as const };
      default:
        return undefined;
    }
  }, []);

  const sourceTypeLegend = [
    { color: { preset: 'light-green' as const }, label: 'Form' },
    { color: { preset: 'light-blue' as const }, label: 'TT Fill' },
  ];

  const set_new_contract = useBearStore((state) => state.set_new_contract);

  // Handler for row clicks to open the layered contract detail modal by id.
  const handleRowClick = (row: TradingContract, _columnId?: string) => {
    setDetailContractId(row.id ?? null);
  };

  // Handler for copying contract to new contract form
  const onOpenNewWith = (row?: any) => {
    set_new_contract(
      !!row ? { ...row.original, id: undefined } : undefined,
    );
  };

  // Copy column definition
  const copyColumn = {
    id: "copy",
    header: "",
    cell: ({ row }: any) => (
      <Button className="" variant="ghost" onClick={() => onOpenNewWith(row)}>
        <CopyIcon className="h-4 w-4" />
      </Button>
    ),
    size: 80,
    enableSorting: true,
    enableHiding: false,
    disableRowClick: true,
    enableColumnFilter: false,
  };

  // Columns for unstaged contracts (no action buttons)
  const unstagedColumns = [copyColumn, ...getOtcContractColumns()];

  // Columns for staged contracts (with action buttons)
  const stagedColumns = [
    copyColumn,
    ...getOtcContractColumns({
      showUnstageButton: true,
      showDeleteButton: true,
      onUnstage: (id: string) => confirmUnstagePrompt({ id }),
      onDelete: (id: string) => confirmDeletePrompt({ id }),
    }),
  ];

  // Custom toolbar buttons for filtering
  const customToolbarButtons = [
    ({ table }: { table: any }) => (
      <div className="flex gap-2">
        <Button
          variant={isOpenRolledFilterActive ? "default" : "outline"}
          size="sm"
          onClick={() => setIsOpenRolledFilterActive(!isOpenRolledFilterActive)}
        >
          <Filter className="h-4 w-4 mr-2" />
          Open Rolled
        </Button>
        {isOpenRolledFilterActive && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsOpenRolledFilterActive(false)}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset Filters
          </Button>
        )}
      </div>
    ),
  ];

  return (
    <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
      <div className="flex flex-row justify-between items-center mb-4">
        {showTitle && (
          <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
            OTC Contracts
          </h1>
        )}
        <OtcContractFormModal
          isNew={true}
          customers={customers}
          accounts={accounts}
          refetchContracts={refetchContracts}
        />
      </div>

      <OtcContractModals
        unstageModalId={unstageModalId}
        deleteModalId={deleteModalId}
        setUnstageModalId={setUnstageModalId}
        setDeleteModalId={setDeleteModalId}
        isUnstaging={isUnstaging}
        isDeleting={isDeleting}
        onUnstage={onUnstage}
        onDelete={onDelete}
      />

      {/* Layered contract detail modal (view/edit by id) */}
      <OtcContractDetailModal
        open={detailContractId !== null}
        contract_id={detailContractId}
        onClose={() => setDetailContractId(null)}
      />

      <Tabs value={tabValue} onValueChange={setTabValue} className="w-full mt-4">
        <TabsList>
          <TabsTrigger value="unstaged">Contracts</TabsTrigger>
          <TabsTrigger value="staged">Review</TabsTrigger>
        </TabsList>

        <TabsContent value="unstaged">
          <DataTable
            stickyHeaders
            tableClassName="max-h-[calc(100vh-400px)] overflow-y-scroll"
            data={filteredContracts}
            columns={unstagedColumns}
            rowBgColor={contractRowBgColor}
            legend={sourceTypeLegend}
            enableColumnResizing
            enableMultiSelect
            enableSorting
            enableMultiSort
            enableFiltering
            enableGrouping
            enableColumnVisibility
            enableMultiFilter
            defaultCondensed
            virtualizationRowHeight={49}
            virtualizationOverscan={2}
            enableVirtualization
            enableColumnPinning
            enableExport
            exportFilename={() => "otc_contracts"}
            defaultColumnVisibility={{ contract_no_nulls: false }}
            defaultSorting={[{ id: "contract_no_nulls", desc: false }, { id: "date", desc: true }]}
            filterStyle="select"
            filterMode="internal"
            customToolbarButtons={customToolbarButtons}
            emptyMessage="No contracts available"
            rowClickable={true}
            onRowClick={handleRowClick}
          />
        </TabsContent>

        <TabsContent value="staged">
          <DataTable
            stickyHeaders
            tableClassName="max-h-[calc(100vh-400px)] overflow-y-scroll"
            data={filteredStagedContracts}
            columns={stagedColumns}
            rowBgColor={contractRowBgColor}
            legend={sourceTypeLegend}
            enableColumnResizing
            enableMultiSelect
            useSteppedGroupingExpansion
            defaultCondensed
            enableSorting
            enableMultiSort
            enableMultiFilter
            enableFiltering
            enableGrouping
            enableColumnVisibility
            enableColumnPinning
            defaultSorting={[{ id: "date", desc: true }]}
            enableExport
            exportFilename={() => "otc_staged_contracts"}
            filterStyle="select"
            filterMode="internal"
            customToolbarButtons={customToolbarButtons}
            emptyMessage="No staged contracts for review"
            rowClickable={true}
            onRowClick={handleRowClick}
          />
        </TabsContent>
      </Tabs>
    </section>
  );
}

